// The Partner — a draggable, page-aware chat widget over the streaming chat
// Lambda (docs/design/2026-07-17-agent-panel.md, P1 Task 5).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Message } from "@ai-sdk/react";
import { useChat } from "@ai-sdk/react";
import { Icon } from "../design/ui";
import { getAuthHeaders } from "../lib/auth";
import { useAgent } from "./AgentProvider";
import { RefMarkdown } from "./RefText";
import { ProposalPanel, parseProposals } from "./ProposalPanel";
import { ActionPanel, parseAgentActions } from "./ActionPanel";
import { useSpeechInput } from "./useSpeechInput";
import { useCorrespondence } from "../correspondence/CorrespondenceProvider";
import { useCorrespondenceCount } from "./useCorrespondenceCount";
import { CorrespondencePanel } from "./CorrespondencePanel";
import "./agent.css";

const CHAT_URL = import.meta.env.VITE_CHAT_URL as string | undefined;
const DOCK_W_KEY = "agent-dock-w";
const CONV_KEY = "agent-conv";
const CONTEXT_PREFIX = "[context] moved to ";
const NUDGE_PREFIX = "[nudge] ";
const PROACTIVE_KEY = "agent-proactive";

// Docked right-hand column — full height below the top bar, drag-resizable width.
const TOP_BAR = 70; // keep in step with brainTokens.layout.topBarHeight
const DOCK_DEFAULT_W = 420;
const DOCK_MIN_W = 320;
const DOCK_MAX_W = 680;

// Muted one-liner labels for tool activity.
const TOOL_LABEL: Record<string, string> = {
  search_thoughts: "searched the brain",
  answer_question: "researched the brain",
  browse_recent: "browsed recent thoughts",
  stats: "checked brain stats",
  capture_thought: "saved a thought",
  list_customers: "listed customers",
  get_customer: "fetched a customer",
  get_project: "fetched a project",
  project_status: "checked project status",
  customer_visits: "fetched site visits",
  customer_tickets: "fetched tickets",
  list_contradictions: "checked contradictions",
  finance_customer: "fetched finance",
  finance_overview: "fetched finance overview",
  system_map: "mapped the system",
  get_board: "read the board",
  project_actions: "read the action register",
  project_followups: "read followups",
  customer_worklogs: "read the work log",
  get_person: "looked up a person",
  get_customer_integrations: "read integrations",
  list_standups: "listed standups",
  bus_activity: "checked agent activity",
};

// Which arg field reads as the "subject" of each tool call, for the live trace
// (S3). Model-generated text rendered as text — no injection surface.
const TOOL_ARG_KEY: Record<string, string> = {
  search_thoughts: "query",
  answer_question: "question",
  similar_tickets: "text",
  get_customer: "customer_id",
  get_project: "project_id",
  project_status: "customer_id",
  project_actions: "project_id",
  project_followups: "project_id",
  customer_worklogs: "customer_id",
  get_person: "slug",
  get_customer_integrations: "customer",
  get_board: "customer",
  customer_visits: "customer_id",
  customer_tickets: "customer_id",
  finance_customer: "customer_id",
  capture_thought: "text",
};

// Concatenated text of a message — from parts, falling back to content for
// hydrated messages that have no parts.
function messageText(m: Message): string {
  const textParts = (m.parts ?? []).filter(
    (p): p is Extract<typeof p, { type: "text" }> => p.type === "text",
  );
  return textParts.length ? textParts.map((p) => p.text).join("") : m.content;
}

function toolDetail(toolName: string, args: unknown): string | undefined {
  const key = TOOL_ARG_KEY[toolName];
  if (!key || typeof args !== "object" || args === null) return undefined;
  const v = (args as Record<string, unknown>)[key];
  if (typeof v !== "string" || !v.trim()) return undefined;
  return v.length > 48 ? `${v.slice(0, 47)}…` : v;
}

// useChat's fetch, with a fresh (auto-refreshed) auth token per request.
const authedFetch: typeof fetch = async (input, init) => {
  const auth = await getAuthHeaders();
  return fetch(input, { ...init, headers: { ...(init?.headers as Record<string, string>), ...auth } });
};

const clampWidth = (w: number) => Math.min(DOCK_MAX_W, Math.max(DOCK_MIN_W, w));

function loadWidth(): number {
  try {
    const raw = localStorage.getItem(DOCK_W_KEY);
    if (raw) {
      const n = parseInt(raw, 10);
      if (Number.isFinite(n)) return clampWidth(n);
    }
  } catch { /* ignore */ }
  return DOCK_DEFAULT_W;
}

function loadConv(): Message[] {
  try {
    const raw = sessionStorage.getItem(CONV_KEY);
    if (raw) return JSON.parse(raw) as Message[];
  } catch { /* ignore */ }
  return [];
}

function tabLabel(tab?: string): string | undefined {
  if (!tab) return undefined;
  return tab.charAt(0).toUpperCase() + tab.slice(1);
}

export function AgentWidget() {
  const {
    pageContext, pageContextRef, open, openAgent, closeAgent,
    pendingSeed, consumeSeed, pendingContextNote, consumeContextNote,
    pendingReference, pendingReferenceRef, clearReference,
  } = useAgent();

  const [width, setWidth] = useState<number>(loadWidth);
  const [tab, setTab] = useState<"agent" | "correspondence">("agent");
  const { target: corrTarget, openNonce } = useCorrespondence();
  const corrCount = useCorrespondenceCount(corrTarget);

  // A Desk row (or anything) can ask the dock to open onto the Correspondence tab.
  useEffect(() => {
    if (openNonce > 0) {
      setTab("correspondence");
      openAgent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openNonce]);
  const modelHintRef = useRef<"default" | "smart">("default");
  const scrollRef = useRef<HTMLDivElement>(null);
  // Presentation mode derived from the seed kind (Phase 5.1) — rendering only.
  const [mode, setMode] = useState<"chat" | "draft" | "explain">("chat");
  const [draftTicket, setDraftTicket] = useState<{ key: string; summary: string } | null>(null);
  const [explainTopic, setExplainTopic] = useState<string>("");
  const [copied, setCopied] = useState(false);
  const [proactive, setProactive] = useState(() => localStorage.getItem(PROACTIVE_KEY) === "1");
  const lastSeedPromptRef = useRef<string>("");
  const lastNudgeEntityRef = useRef<string | null>(null);
  const busyRef = useRef(false);

  const { messages, input, setInput, handleInputChange, handleSubmit, append, setMessages, status, error, stop } =
    useChat({
      api: CHAT_URL,
      streamProtocol: "data",
      fetch: authedFetch,
      initialMessages: loadConv(),
      maxSteps: 5,
    });

  // Persist the transcript for the tab's lifetime (survives refresh, dies with tab).
  useEffect(() => {
    try { sessionStorage.setItem(CONV_KEY, JSON.stringify(messages)); } catch { /* ignore */ }
  }, [messages]);

  // Autoscroll on new content.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, open]);

  useEffect(() => { busyRef.current = status === "submitted" || status === "streaming"; }, [status]);

  // Voice (P2): final transcripts append into the composer for review — the
  // human still presses Send. useChat's setInput takes a plain string, so track
  // the live value in a ref to avoid stale-closure appends mid-dictation.
  const inputRef = useRef(input);
  inputRef.current = input;
  const speech = useSpeechInput((text) => {
    const prev = inputRef.current.trim();
    setInput(prev ? `${prev} ${text}` : text);
  });

  const toggleProactive = () => {
    setProactive((on) => {
      const next = !on;
      try { next ? localStorage.setItem(PROACTIVE_KEY, "1") : localStorage.removeItem(PROACTIVE_KEY); } catch { /* ignore */ }
      return next;
    });
  };

  // Ambient nudge (S4, opt-in): arriving at a new entity with proactive on, ask
  // Haiku for ONE useful line after a 5s settle. Never fires while busy.
  const entityId = pageContext?.entity?.id;
  const entityName = pageContext?.entity?.name ?? entityId;
  useEffect(() => {
    if (!open || !proactive || !entityId) return;
    if (lastNudgeEntityRef.current === entityId) return;
    lastNudgeEntityRef.current = entityId;
    const t = setTimeout(() => {
      if (busyRef.current) return;
      modelHintRef.current = "default"; // Haiku only — cheap ambient call
      void append({ role: "user", content: `${NUDGE_PREFIX}${entityName}` }, { body: bodyFor("default") });
    }, 5000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, proactive, entityId]);

  const bodyFor = useCallback(
    (hint: "default" | "smart") => {
      const ctx = pageContextRef.current;
      const ref = pendingReferenceRef.current;
      // A pointed-at item (S2) rides under snapshot.focus for the next message.
      const context = ref
        ? { ...ctx, snapshot: { ...(ctx?.snapshot ?? {}), focus: ref.snapshot } }
        : ctx;
      return { context, modelHint: hint };
    },
    [pageContextRef, pendingReferenceRef],
  );

  // Seeds: explain → auto-send (smart model); ask → prefill the composer.
  useEffect(() => {
    if (!open) return;
    const seed = consumeSeed();
    if (!seed) return;
    if (seed.kind === "explain" || seed.kind === "draft-ticket") {
      // draft-ticket + explain seeds carry a prompt; auto-send (smart model) and set
      // the presentation mode. The confidence note + citations live in the streamed
      // reply (the prompt asks for them) — no server changes.
      const prompt = seed.prompt ?? `Explain this: ${JSON.stringify(seed.snapshot ?? {})}`;
      modelHintRef.current = seed.modelHint === "default" ? "default" : "smart";
      lastSeedPromptRef.current = prompt;
      if (seed.kind === "draft-ticket") {
        setMode("draft");
        setDraftTicket(seed.ticket ?? null);
      } else {
        setMode("explain");
        setExplainTopic(String(seed.snapshot?.label ?? "this"));
      }
      void append({ role: "user", content: prompt }, { body: bodyFor(modelHintRef.current) });
    } else {
      setMode("chat");
      setInput(seed.prompt ?? "");
    }
  }, [open, pendingSeed, consumeSeed, append, setInput, bodyFor]);

  // Page changed while open → drop a context marker into the transcript. It rides
  // with the NEXT user message; the system prompt tells the model not to reply to it.
  useEffect(() => {
    const note = consumeContextNote();
    if (!note) return;
    setMessages((prev) => [
      ...prev,
      { id: `ctx-${prev.length}-${note}`, role: "user", content: `${CONTEXT_PREFIX}${note}` } as Message,
    ]);
  }, [pendingContextNote, consumeContextNote, setMessages]);

  // ── Left-edge resize — dragging left widens the dock ───────────────────────
  const resizeRef = useRef<{ startX: number; baseW: number } | null>(null);
  const onResizeDown = (e: React.PointerEvent) => {
    e.preventDefault();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    resizeRef.current = { startX: e.clientX, baseW: width };
  };
  const onResizeMove = (e: React.PointerEvent) => {
    const r = resizeRef.current;
    if (!r) return;
    setWidth(clampWidth(r.baseW - (e.clientX - r.startX)));
  };
  const onResizeUp = () => {
    if (resizeRef.current) {
      resizeRef.current = null;
      try { localStorage.setItem(DOCK_W_KEY, String(width)); } catch { /* ignore */ }
    }
  };

  const contextChip = useMemo(() => {
    if (!pageContext) return "with you on: Today";
    const where = pageContext.entity?.name ?? pageContext.entity?.id;
    const tab = tabLabel(pageContext.tab);
    if (where && tab) return `with you on: ${where} · ${tab}`;
    if (where) return `with you on: ${where}`;
    // No entity — name the page from the route ("/standup/history" → "Standup").
    const page = pageContext.route.split("/").filter(Boolean)[0]?.replace(/-mui$/, "");
    return page ? `with you on: ${tabLabel(page)}` : "with you on: Today";
  }, [pageContext]);

  // Context-aware conversation starters — shown in the empty state and as a
  // compact chip bar above the composer once a thread exists (P2).
  const suggestions = useMemo(() => {
    const name = pageContext?.entity?.name ?? pageContext?.entity?.id;
    const type = pageContext?.entity?.type;
    const tab = pageContext?.tab;
    if (type === "project" && tab === "plan") {
      return [
        "Catch me up on this plan — what needs my attention?",
        "Check open feedback against the knowledge base",
        "What does this plan contradict in the brain?",
      ];
    }
    if (type === "project" && tab === "actions") {
      return [
        "What's blocking this project?",
        "Summarise the open actions by workstream",
        "Which actions are waiting on the customer?",
      ];
    }
    if (type === "project") {
      return [
        `Catch me up on ${name ?? "this project"}`,
        "What's blocking this project?",
        "What should happen next here?",
      ];
    }
    if (type === "customer" && tab === "log") {
      return [
        "Summarise this month's logged work",
        "Any unbilled or unusual entries here?",
        `Catch me up on ${name}`,
      ];
    }
    if (type === "customer" && name) {
      return [
        "What happened during the last site visit?",
        `Any open tickets for ${name}?`,
        `Catch me up on ${name}`,
      ];
    }
    if (pageContext?.route === "/standup") {
      return [
        "What does the brain want resolved today?",
        "What moved since yesterday?",
        "Which projects are P1 right now?",
      ];
    }
    return ["What's changed recently?", "What's in the brain?", "Show me the project portfolio"];
  }, [pageContext]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;
    modelHintRef.current = "default";
    handleSubmit(e, { body: bodyFor("default") });
    clearReference(); // the pointed-at item rode with this message; drop the pill
  };

  const clearConversation = () => {
    setMessages([]);
    setMode("chat");
    setDraftTicket(null);
    try { sessionStorage.removeItem(CONV_KEY); } catch { /* ignore */ }
  };

  // Regenerate the current draft — re-send the seed prompt (smart model).
  const regenerate = () => {
    if (!lastSeedPromptRef.current) return;
    modelHintRef.current = "smart";
    void append({ role: "user", content: lastSeedPromptRef.current }, { body: bodyFor("smart") });
  };

  // Ask the model to distill the conversation into savable thoughts (W2). The
  // reply is an agent-proposals block the widget renders as an editable panel.
  const saveToBrain = () => {
    modelHintRef.current = "smart";
    void append({ role: "user", content: "Save this conversation to the brain." }, { body: bodyFor("smart") });
  };

  // Copy the drafted reply for pasting into Jira (W1) — the agent never posts.
  // Markers/card blocks are stripped so the human pastes clean prose.
  const copyReply = () => {
    const last = [...messages].reverse().find((m) => m.role === "assistant");
    if (!last) return;
    const text = messageText(last)
      .replace(/```agent-card[\s\S]*?```/g, "")
      .replace(/\[(?:ref|goto):[a-z_]+:[^\]]+\]/g, "")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
    void navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  };

  // Collapsed — a slim full-height rail on the right edge that opens the dock.
  // When the current record has correspondence, a 💬 chip surfaces it and opens
  // straight to that tab.
  if (!open) {
    return (
      <aside className="agent-rail" aria-label="Agent" style={{ top: TOP_BAR, height: `calc(100vh - ${TOP_BAR}px)` }}>
        <button
          type="button"
          className="agent-rail-btn"
          aria-label="Open the agent"
          title="Ask the agent"
          onClick={() => { setTab("agent"); openAgent(); }}
        >
          <Icon name="sparkles" size={18} />
          <span className="agent-rail-label">Ask the agent</span>
        </button>
        {corrCount > 0 && (
          <button
            type="button"
            className="agent-rail-corr"
            aria-label={`Correspondence (${corrCount})`}
            title={`Correspondence · ${corrCount}`}
            onClick={() => { setTab("correspondence"); openAgent(); }}
          >
            <Icon name="messages" size={16} />
            <span className="agent-rail-count">{corrCount}</span>
          </button>
        )}
      </aside>
    );
  }

  const busy = status === "submitted" || status === "streaming";

  // Save-to-brain proposals (W2): rendered from the latest assistant reply that
  // carries an agent-proposals block. Keyed by message id so edits reset per run.
  const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant");
  const proposals = lastAssistant ? parseProposals(messageText(lastAssistant)) : null;
  // Mutation confirm cards (P1): an agent-action block on the latest reply.
  const actionProposals = lastAssistant ? parseAgentActions(messageText(lastAssistant)) : null;
  const proposalKey = lastAssistant?.id ?? "none";

  return (
    <aside
      className={`agent-dock agent-widget--${mode}`}
      style={{ width, top: TOP_BAR, height: `calc(100vh - ${TOP_BAR}px)` }}
      role="dialog"
      aria-label="Aeronet agent"
    >
      <div
        className="agent-dock-resize"
        onPointerDown={onResizeDown}
        onPointerMove={onResizeMove}
        onPointerUp={onResizeUp}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize agent panel"
      />
      <div className="agent-dock-inner">
      <div className="dock-tabs">
        <button
          type="button"
          className={`dock-tab${tab === "agent" ? " active" : ""}`}
          onClick={() => setTab("agent")}
        >
          <Icon name="sparkles" size={14} /> Agent
        </button>
        <button
          type="button"
          className={`dock-tab${tab === "correspondence" ? " active" : ""}`}
          onClick={() => setTab("correspondence")}
        >
          <Icon name="messages" size={14} /> Correspondence{corrCount > 0 ? ` · ${corrCount}` : ""}
        </button>
        <div style={{ flex: 1 }} />
        <button type="button" className="agent-icon-btn" aria-label="Close panel" title="Close" onClick={closeAgent}>
          <Icon name="minus" size={15} />
        </button>
      </div>
      {tab === "correspondence" ? (
        <CorrespondencePanel />
      ) : (
      <>
      <header className="agent-head">
        <div className="agent-head-title">
          <span className="agent-avatar"><Icon name="sparkles" size={15} /></span>
          <span className="agent-context-chip">{contextChip}</span>
        </div>
        <div className="agent-head-actions">
          <button
            type="button"
            className={`agent-icon-btn${proactive ? " agent-icon-btn--on" : ""}`}
            aria-pressed={proactive}
            aria-label="Proactive suggestions"
            title={proactive ? "Proactive: on — offers a line when you open a record" : "Proactive: off"}
            onClick={toggleProactive}
          >
            <Icon name="sparkles" size={15} />
          </button>
          {messages.some((m) => m.role === "assistant") && (
            <button type="button" className="agent-icon-btn" aria-label="Save conversation to brain" title="Save conversation to brain" onClick={saveToBrain}>
              <Icon name="bookmark" size={15} />
            </button>
          )}
          <button type="button" className="agent-icon-btn" aria-label="Clear conversation" title="Clear conversation" onClick={clearConversation}>
            <Icon name="eraser" size={15} />
          </button>
        </div>
      </header>

      <div className="agent-scroll" ref={scrollRef}>
        {mode === "draft" && (
          <div className="agent-mode-banner">Drafting reply · <b>{draftTicket?.key ?? "ticket"}</b></div>
        )}
        {mode === "explain" && (
          <div className="agent-mode-banner agent-mode-banner--explain">Explaining · <b>{explainTopic}</b></div>
        )}
        {messages.length === 0 && (
          <div className="agent-empty">
            <div className="agent-empty-lead">Ask me about what you're looking at — customers, projects, tickets, visits, or anything in the brain.</div>
            <div className="agent-suggestions">
              {suggestions.map((s) => (
                <button
                  key={s}
                  type="button"
                  className="agent-suggestion"
                  onClick={() => {
                    modelHintRef.current = "default";
                    void append({ role: "user", content: s }, { body: bodyFor("default") });
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <MessageRow key={m.id} message={m} streaming={busy && i === messages.length - 1} />
        ))}
        {status === "submitted" && <div className="agent-tool-line agent-tool-line--live"><span className="agent-tool-dot" />thinking</div>}
        {error && <div className="agent-error">Something went wrong. Try again.</div>}
        {!busy && proposals && <ProposalPanel key={proposalKey} proposals={proposals} />}
        {!busy && actionProposals && (
          <ActionPanel key={`act-${proposalKey}`} proposals={actionProposals} />
        )}
        {mode === "draft" && !busy && messages.some((m) => m.role === "assistant") && (
          <div className="agent-draft-actions">
            <button type="button" className="agent-insert-btn" onClick={copyReply} title="Copy the drafted reply to paste into Jira">
              {copied ? "Copied ✓" : "Copy reply"}
            </button>
            <button type="button" className="agent-regen-btn" onClick={regenerate}>Regenerate</button>
          </div>
        )}
      </div>

      {!busy && messages.length > 0 && mode === "chat" && (
        <div className="agent-suggestions agent-suggestions--bar">
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              className="agent-suggestion"
              onClick={() => {
                modelHintRef.current = "default";
                void append({ role: "user", content: s }, { body: bodyFor("default") });
              }}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {pendingReference && (
        <div className="agent-ref-pill-bar">
          <span className="agent-ref-pill">
            <Icon name="link" size={12} />
            re: {pendingReference.label}
            <button type="button" className="agent-ref-pill-x" aria-label="Remove reference" onClick={clearReference}>
              <Icon name="x" size={11} />
            </button>
          </span>
        </div>
      )}

      {speech.error && <div className="agent-error" style={{ margin: "0 12px 6px" }}>{speech.error}</div>}

      <form className="agent-composer" onSubmit={submit}>
        <input
          className="agent-input"
          value={input}
          onChange={handleInputChange}
          placeholder={CHAT_URL ? "Ask the agent…" : "Chat URL not configured"}
          disabled={!CHAT_URL}
          aria-label="Message the agent"
        />
        {speech.supported && (
          <button
            type="button"
            className={`agent-icon-btn${speech.listening ? " agent-icon-btn--on agent-mic--live" : ""}`}
            aria-pressed={speech.listening}
            aria-label={speech.listening ? "Stop dictating" : "Dictate"}
            title={speech.error ?? (speech.listening ? "Recording — click to stop" : speech.transcribing ? "Transcribing…" : "Dictate")}
            disabled={!CHAT_URL}
            onClick={speech.toggle}
          >
            <Icon name={speech.listening ? "microphone-filled" : "microphone"} size={16} />
          </button>
        )}
        {busy ? (
          <button type="button" className="agent-send" aria-label="Stop" onClick={stop}><Icon name="player-stop" size={16} /></button>
        ) : (
          <button type="submit" className="agent-send" aria-label="Send" disabled={!CHAT_URL || !input.trim()}><Icon name="send" size={16} /></button>
        )}
      </form>
      </>
      )}
      </div>
    </aside>
  );
}

function MessageRow({ message, streaming }: { message: Message; streaming?: boolean }) {
  if (message.role === "user" && message.content.startsWith(CONTEXT_PREFIX)) {
    return <div className="agent-divider"><Icon name="arrow-right" size={12} />{message.content.slice(CONTEXT_PREFIX.length)}</div>;
  }
  // The ambient-nudge request (S4) is a hidden prompt — never shown to the user.
  if (message.role === "user" && message.content.startsWith(NUDGE_PREFIX)) return null;
  if (message.role !== "user" && message.role !== "assistant") return null;

  const isUser = message.role === "user";
  const parts = message.parts ?? [];
  const tools = parts
    .filter((p): p is Extract<typeof p, { type: "tool-invocation" }> => p.type === "tool-invocation")
    .map((p) => ({
      label: TOOL_LABEL[p.toolInvocation.toolName] ?? `used ${p.toolInvocation.toolName}`,
      detail: toolDetail(p.toolInvocation.toolName, p.toolInvocation.args),
      running: p.toolInvocation.state !== "result",
    }));
  const text = messageText(message);

  if (isUser) {
    return (
      <div className="agent-msg agent-msg--user">
        <div className="agent-bubble">{text}</div>
      </div>
    );
  }
  // A nudge that found nothing collapses to nothing (S4).
  if (text.trim() === "NOTHING") return null;
  return (
    <div className="agent-msg agent-msg--assistant">
      {tools.map((t, i) => (
        <div key={i} className={`agent-tool-line${t.running ? " agent-tool-line--live" : ""}`}>
          <span className="agent-tool-dot" />{t.label}
          {t.detail && <span className="agent-tool-arg">: {t.detail}</span>}
        </div>
      ))}
      {text && (
        <div className={`agent-out${streaming ? " agent-out--streaming" : ""}`}>
          <RefMarkdown text={text} />
        </div>
      )}
    </div>
  );
}
